import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { nextSicuro } from '@/lib/nav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Mail } from 'lucide-react';

const LOGO_BLACK = '/logo-biounica-nero.png';

export default function LoginPage() {
  const { login, requestMagicLink, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Pagina annunciata dal link che ha portato qui: ci si torna dopo l'accesso.
  const dest = nextSicuro(params.get('next')) || '/dashboard';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);

  useEffect(() => {
    if (user?.profile?.is_approved) navigate(dest, { replace: true });
  }, [user, navigate, dest]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(email, password);
      if (!userData.profile?.is_approved) {
        toast.info(
          userData.profile?.blocked_reason
            || 'Il tuo accesso al portale non risulta attivo. Se sei un promotore Biounica, contatta Biounica.'
        );
        return;
      }
      navigate(dest);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il login');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async () => {
    if (!email) {
      toast.error('Inserisci la tua email per ricevere il link di accesso.');
      return;
    }
    setMagicLoading(true);
    try {
      await requestMagicLink(email, dest === '/dashboard' ? null : dest);
      toast.success("Se l'email è di un promotore Biounica, riceverai un link per accedere.");
    } catch {
      toast.error('Si è verificato un errore. Riprova.');
    } finally {
      setMagicLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#efe8da] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_BLACK} alt="Biounica" className="h-14 mx-auto mb-4" />
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#12100c]">Accedi al portale</h1>
          <p className="text-sm text-[#4a4238] mt-1">Area riservata ai promotori Biounica</p>
        </div>

        <Card className="rounded-2xl shadow-sm border-gray-100">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-[#12100c] font-medium text-sm">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="la-tua@email.it"
                  required
                  className="mt-1.5 rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b]"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-[#12100c] font-medium text-sm">
                  Password
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="La tua password"
                    required
                    className="rounded-xl border-gray-300 focus:border-[#c6a15b] focus:ring-[#c6a15b] pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <Link
                  to="/reset-password"
                  className="text-sm text-[#4a5f43] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Hai dimenticato la password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full h-11 font-medium transition-colors"
                data-testid="login-submit-btn"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Accedi
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-[#5b5346]">oppure</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleMagicLink}
              disabled={magicLoading}
              className="w-full rounded-full h-11 font-medium border-[#12100c]/20 text-[#12100c] hover:bg-[#12100c]/5"
              data-testid="magic-link-btn"
            >
              {magicLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
              Accedi con un link via email
            </Button>

            <p className="mt-4 text-center text-xs text-[#5b5346]">
              Sei un promotore Biounica? Inserisci la tua email e ricevi un link di accesso, senza password.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
